"use client";

import { useState, type ChangeEvent, useRef } from "react";
import * as pdfjs from "pdfjs-dist";

// Set workerSrc to avoid issues with Next.js.
// This is a common pattern for using pdf.js in a webpacked environment.
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PdfImporterProps {
  onScriptImported: (
    title: string,
    content: string,
    preParsedBlocks?: any[],
  ) => void;
  disabled?: boolean
  children: React.ReactNode;
  className?: string;
}

export function PdfImporter({ onScriptImported, disabled, children, className }: PdfImporterProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      if (file.name.toLowerCase().endsWith(".sbx")) {
        let text = await file.text();

        // Decode HTML entities if the SBX was saved with encoded brackets
        if (text.includes("&lt;div")) {
          const textarea = document.createElement("textarea");
          textarea.innerHTML = text;
          text = textarea.value;
        }

        const parser = new DOMParser();
        const doc = parser.parseFromString(text, "text/html");

        const divs = Array.from(doc.querySelectorAll("div"));
        const preParsedBlocks: any[] = [];

        const typeMap: Record<string, string> = {
          divtype0: "SCENE_HEADING",
          divtype2: "ACTION",
          divtype3: "CHARACTER",
          divtype4: "PARENTHETICAL",
          divtype5: "DIALOGUE",
          divtype6: "TRANSITION",
        };

        divs.forEach((div) => {
          let divText = div.textContent?.trim() || "";
          if (!divText) return; // Skip completely empty blocks

          let type = "ACTION"; // Default
          for (const className of Array.from(div.classList)) {
            if (typeMap[className]) {
              type = typeMap[className];
              break;
            }
          }

          const block: any = { type, text: divText };

          if (type === "SCENE_HEADING") {
            const sceneNum = div.getAttribute("data-scene");
            if (sceneNum) block.sceneNumber = sceneNum;

            let parsedText = divText;
            const typeMatch = parsedText.match(/^(INT\/EXT|INT|EXT)\.?\s+/i);
            if (typeMatch) {
              let sType = typeMatch[1].toUpperCase();
              if (!sType.endsWith(".")) sType += ".";
              block.sceneType = sType;
              parsedText = parsedText.substring(typeMatch[0].length).trim();
            }

            const timeMatch = parsedText.match(/\s+-\s+([^-]+)$/);
            if (timeMatch) {
              block.timeOfDay = timeMatch[1].trim().toUpperCase();
              parsedText = parsedText.substring(0, timeMatch.index).trim();
            }

            block.text = parsedText;
          }

          preParsedBlocks.push(block);
        });

        const title = file.name.replace(/\.sbx$/i, "");
        onScriptImported(title.trim(), "", preParsedBlocks);
      } else {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjs.getDocument(arrayBuffer).promise;

        const processPage = async (pageNumber: number): Promise<string> => {
          const page = await pdf.getPage(pageNumber);
          const content = await page.getTextContent();
          const items = content.items.filter(
            (item): item is pdfjs.TextItem =>
              "str" in item && item.str.trim().length > 0,
          );

          if (items.length === 0) return "";

          const lines: { y: number; items: { x: number; text: string }[] }[] =
            [];

          for (const item of items) {
            let found = false;
            for (const line of lines) {
              if (Math.abs(line.y - item.transform[5]) < 5) {
                // 5px tolerance
                line.items.push({ x: item.transform[4], text: item.str });
                found = true;
                break;
              }
            }
            if (!found) {
              lines.push({
                y: item.transform[5],
                items: [{ x: item.transform[4], text: item.str }],
              });
            }
          }

          lines.sort((a, b) => b.y - a.y);

          return lines
            .map((line) => {
              line.items.sort((a, b) => a.x - b.x);
              return line.items.map((item) => item.text).join(" ");
            })
            .join("\n");
        };

        let title = "";
        if (pdf.numPages > 0) {
          title = await processPage(1);
        }

        let scriptContent = "";
        for (let i = 1; i <= pdf.numPages; i++) {
          scriptContent += (await processPage(i)) + "\n\n";
        }

        onScriptImported(title.trim(), scriptContent);
      }
    } catch (err) {
      console.error("Error processing PDF:", err);
      setError(
        err instanceof Error
          ? `Error processing PDF: ${err.message}`
          : "An unknown error occurred.",
      );
    } finally {
      setIsProcessing(false);
      // Reset file input so the same file can be selected again
      if (event.target) {
        event.target.value = "";
      }
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,application/pdf"
        onChange={handleFileChange}
        disabled={isProcessing || disabled}
        className="hidden"
        id="pdf-importer-input"
      />
      <button
        onClick={handleClick}
        disabled={isProcessing || disabled}
        className={className || `flex items-center justify-center transition-all duration-200 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 ${isProcessing ? "w-auto px-3 sm:px-4 h-8 sm:h-10 rounded-full text-sm font-semibold bg-zinc-100 text-zinc-500" : "w-8 h-8 sm:w-10 sm:h-10 rounded-full text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"}`}
        aria-label="Import Script"
      >
        {isProcessing ? (
          <span className="text-xs sm:text-sm font-semibold">Processing...</span>
        ) : (
          children
        )}
      </button>
      {error && <p className="sr-only">Error: {error}</p>}
    </>
  );
}
